import helpImg from '@/assets/img/curved-images/white-curved.jpg';
import brandImg from '@/assets/img/logo.svg';
import { useMenuListedTeams } from '@/hooks/api/2260_menu_listed_teams';
import { useMeTeamMembers } from '@/hooks/api/2460_me_team_members';
import { Team } from '@/hooks/api/models';
import {
  defaultApprovalSearchCondition,
  useApprovalSearchCondition,
} from '@/providers/ApprovalSearchConditionProvider';
import {
  defaultInvoiceTaskSearchCondition,
  defaultPaymentTaskSearchCondition,
  defaultPurchaseOrderContractTaskSearchCondition,
  defaultQuotationTaskSearchCondition,
  defaultSalesOrderContractTaskSearchCondition,
  useBackofficeSearchCondition,
} from '@/providers/BackofficeSearchConditionProvider';
import { useNavState } from '@/providers/NavStateProvider';
import { useCurrentWorkspace } from '@/providers/WorkspaceProvider';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

export const SideNav = () => {
  const location = useLocation();
  const { _w_, me } = useCurrentWorkspace();
  const { sideNavCollapse, setSideNavCollapse, sideNavMenuSelectState } = useNavState();
  // const { data: salesOrderTeams } = useTeamsSalesOrderMenuListed({
  //   identifier: _w_!.identifier,
  // });
  // const { data: purchaseOrderTeams } = useTeamsPurchaseOrderMenuListed({
  //   identifier: _w_!.identifier,
  // });

  const { data: menuListedTeams } = useMenuListedTeams({ identifier: _w_!.identifier });
  const { data: teamMembers } = useMeTeamMembers({ identifier: _w_!.identifier });
  const [salesOrderTeams, setSalesOrderTeams] = useState<Team[]>([]);
  const [purchaseOrderTeams, setPurchaseOrderTeams] = useState<Team[]>([]);

  useEffect(() => {
    if (menuListedTeams) {
      setSalesOrderTeams(menuListedTeams.sales_order_teams);
      setPurchaseOrderTeams(menuListedTeams.purchase_order_teams);
    }
  }, [menuListedTeams]);

  const hasApproverRole = useMemo(() => {
    if (teamMembers == null) {
      return false;
    } else {
      const roles = teamMembers.map((d) => d.roles).flat();
      return !!roles.find((role) => role.name === 'approver');
    }
  }, [teamMembers]);

  const hasFinancialOfficerRole = useMemo(() => {
    if (teamMembers == null) {
      return false;
    } else {
      const roles = teamMembers.map((d) => d.roles).flat();
      return !!roles.find((role) => role.name === 'financial_officer');
    }
  }, [teamMembers]);

  const hasAdminRole = useMemo(() => {
    if (teamMembers == null) {
      return false;
    } else {
      const roles = teamMembers.map((d) => d.roles).flat();
      return !!roles.find((role) => role.name === 'admin');
    }
  }, [teamMembers]);

  const {
    setPurchaseOrderContractTaskDisplayType,
    setPurchaseOrderContractTaskSearchCondition,

    setPaymentTaskDisplayType,
    setPaymentTaskSearchCondition,

    setSalesOrderContractTaskDisplayType,
    setSalesOrderContractTaskSearchCondition,

    setQuotationTaskDisplayType,
    setQuotationTaskSearchCondition,

    setInvoiceTaskDisplayType,
    setInvoiceTaskSearchCondition,
  } = useBackofficeSearchCondition();

  const { setApprovalDisplayType, setApprovalSearchCondition } = useApprovalSearchCondition();

  const invoiceTasksClick = () => {
    setInvoiceTaskSearchCondition({
      ...defaultInvoiceTaskSearchCondition,
      identifier: _w_!.identifier,
      subscribed: true,
      invoice_task_phase_ids: [1, 2, 3, 4],
    });
  };

  const paymentTasksClick = () => {
    setPaymentTaskSearchCondition({
      ...defaultPaymentTaskSearchCondition,
      identifier: _w_!.identifier,
      subscribed: true,
      payment_task_phase_ids: [1, 2, 4],
    });
  };

  const quotationTasksClick = () => {
    setQuotationTaskSearchCondition({
      ...defaultQuotationTaskSearchCondition,
      identifier: _w_!.identifier,
      subscribed: true,
      quotation_task_phase_ids: [1, 2, 3],
    });
  };

  const salesOrderContractTasksClick = () => {
    setSalesOrderContractTaskSearchCondition({
      ...defaultSalesOrderContractTaskSearchCondition,
      identifier: _w_!.identifier,
      subscribed: true,
      sales_order_contract_task_phase_ids: [1, 4, 5],
    });
  };

  const purchaseOrderContractTasksClick = () => {
    setPurchaseOrderContractTaskSearchCondition({
      ...defaultPurchaseOrderContractTaskSearchCondition,
      identifier: _w_!.identifier,
      subscribed: true,
      purchase_order_contract_task_phase_ids: [1, 4, 5],
    });
  };

  const approvalClick = () => {
    setApprovalSearchCondition({
      ...defaultApprovalSearchCondition,
      identifier: _w_!.identifier,
      user_ids: [me!.id],
    });
  };

  return (
    <aside
      className="sidenav navbar navbar-vertical navbar-expand-xs border-0 border-radius-xl my-3 fixed-start"
      id="sidenav-main"
    >
      <div className="sidenav-header">
        <div className="brand-logo-wrapper">
          <img src={brandImg} className="brand-logo" />
        </div>
        <Link className="navbar-brand" to={`/w/${_w_!.identifier}`}>
          <div className="name-wrapper">
            <img src={_w_!.logo_image_file_url} className="navbar-brand-img h-100 border-radius-sm" alt="main_logo" />
            <div className="ws-name text-xs">{_w_!.name}</div>
          </div>
        </Link>
      </div>
      <hr className="horizontal dark mt-0" />
      <div className="collapse navbar-collapse w-auto h-auto" id="sidenav-collapse-main">
        <ul className="navbar-nav">
          <li className="nav-item">
            <a
              data-bs-toggle="collapse"
              href="#dashboards"
              className={`nav-link ${sideNavMenuSelectState.dashboard && 'active'}`}
              aria-controls="dashboards"
              role="button"
              aria-expanded={sideNavCollapse.dashboard ? 'false' : 'true'}
              onClick={() => {
                setSideNavCollapse({ ...sideNavCollapse, dashboard: !sideNavCollapse.dashboard });
              }}
            >
              <div className="icon icon-sm shadow border-radius-md me-2 bg-white">
                <i className="fas fa-chart-pie"></i>
              </div>
              <span className="nav-link-text ms-1">ダッシュボード</span>
            </a>
            <div className={`collapse ${!sideNavCollapse.dashboard && 'show'}`} id="dashboards">
              <ul className="nav ms-4 ps-3">
                <li className="nav-item">
                  {(() => {
                    const path = `/w/${_w_!.identifier}/dashboard/tasks`;
                    const isActive = location.pathname.startsWith(path) || location.pathname == `/w/${_w_!.identifier}`;
                    return (
                      <Link className={`nav-link ${isActive && 'active'}`} to={path}>
                        <span className="sidenav-mini-icon">タ</span>
                        <span className="sidenav-normal">タスクボード</span>
                      </Link>
                    );
                  })()}
                </li>
                <li className="nav-item">
                  {(() => {
                    const path = `/w/${_w_!.identifier}/dashboard/sales_orders`;
                    const isActive = location.pathname.startsWith(path);
                    return (
                      <Link className={`nav-link ${isActive && 'active'}`} to={path}>
                        <span className="sidenav-mini-icon">売</span>
                        <span className="sidenav-normal">売上ボード</span>
                      </Link>
                    );
                  })()}
                </li>
                <li className="nav-item">
                  {(() => {
                    const path = `/w/${_w_!.identifier}/dashboard/purchase_orders`;
                    const isActive = location.pathname.startsWith(path);
                    return (
                      <Link className={`nav-link ${isActive && 'active'}`} to={path}>
                        <span className="sidenav-mini-icon">費</span>
                        <span className="sidenav-normal">費用ボード</span>
                      </Link>
                    );
                  })()}
                </li>
                <li className="nav-item">
                  {(() => {
                    const path = `/w/${_w_!.identifier}/dashboard/weekly_comparison`;
                    const isActive = location.pathname.startsWith(path);
                    return (
                      <Link className={`nav-link ${isActive && 'active'}`} to={path}>
                        <span className="sidenav-mini-icon">比</span>
                        <span className="sidenav-normal">比較ボード</span>
                      </Link>
                    );
                  })()}
                </li>
                <li className="nav-item">
                  {(() => {
                    const path = `/w/${_w_!.identifier}/dashboard/pl`;
                    const isActive = location.pathname.startsWith(path);
                    return (
                      <Link className={`nav-link ${isActive && 'active'}`} to={path}>
                        <span className="sidenav-mini-icon">差</span>
                        <span className="sidenav-normal">売上-費用ボード</span>
                      </Link>
                    );
                  })()}
                </li>
              </ul>
            </div>
          </li>

          {/*<li className="nav-item">*/}
          {/*  <Link className={`nav-link ${sideNavMenuSelectState.dashboard && 'active'}`} to={`/w/${_w_!.identifier}`}>*/}
          {/*    <div className="icon icon-sm shadow border-radius-md me-2 bg-white">*/}
          {/*      <i className="fas fa-chart-pie"></i>*/}
          {/*    </div>*/}
          {/*    <span className="nav-link-text ms-1">Dashboard</span>*/}
          {/*  </Link>*/}
          {/*</li>*/}

          <li className="nav-item">
            <Link
              data-bs-toggle="collapse"
              to="#sales-orders"
              className={`nav-link ${sideNavMenuSelectState.salesOrders && 'active'}`}
              aria-controls="sales-orders"
              role="button"
              aria-expanded={sideNavCollapse.salesOrders ? 'false' : 'true'}
              onClick={() => {
                setSideNavCollapse({ ...sideNavCollapse, salesOrders: !sideNavCollapse.salesOrders });
              }}
            >
              <div className="icon icon-sm shadow border-radius-md me-2 bg-white">
                <i className="fas fa-folder-open"></i>
              </div>
              <span className="nav-link-text ms-1">プロジェクト（売上）</span>
            </Link>
            <div className={`collapse ${!sideNavCollapse.salesOrders && 'show'}`} id="sales-orders">
              <ul className="nav ms-4 ps-3">
                {salesOrderTeams?.map((t) => {
                  const path = `/w/${_w_!.identifier}/teams/${t.id}/sales_orders`;
                  const isActive = location.pathname.startsWith(path);
                  return (
                    <li key={`so-${t.id}`} className="nav-item">
                      <Link className={`nav-link ${isActive && 'active'}`} to={path}>
                        <span className="sidenav-mini-icon">{t.name.substring(0, 1)}</span>
                        <span className="sidenav-normal">{t.name}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </li>
          <li className="nav-item">
            <Link
              data-bs-toggle="collapse"
              to="#purchase-orders"
              className={`nav-link ${sideNavMenuSelectState.purchaseOrders && 'active'}`}
              aria-controls="purchase-orders"
              role="button"
              aria-expanded={sideNavCollapse.purchaseOrders ? 'false' : 'true'}
              onClick={() => {
                setSideNavCollapse({ ...sideNavCollapse, purchaseOrders: !sideNavCollapse.purchaseOrders });
              }}
            >
              <div className="icon icon-sm shadow border-radius-md me-2 bg-white">
                <i className="fas fa-folder-open"></i>
              </div>
              <span className="nav-link-text ms-1">プロジェクト（費用）</span>
            </Link>
            <div className={`collapse ${!sideNavCollapse.purchaseOrders && 'show'}`} id="purchase-orders">
              <ul className="nav ms-4 ps-3">
                {purchaseOrderTeams?.map((t) => {
                  const path = `/w/${_w_!.identifier}/teams/${t.id}/purchase_orders`;
                  const isActive = location.pathname.startsWith(path);
                  return (
                    <li key={`po-${t.id}`} className="nav-item">
                      <Link className={`nav-link ${isActive && 'active'}`} to={path}>
                        <span className="sidenav-mini-icon">{t.name.substring(0, 1)}</span>
                        <span className="sidenav-normal">{t.name}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </li>
          <li className="nav-item">
            <hr className="horizontal dark" />
          </li>
          <li className="nav-item">
            <a
              data-bs-toggle="collapse"
              href="#backoffice-tasks"
              className={`nav-link ${sideNavMenuSelectState.backoffice && 'active'}`}
              aria-controls="backoffice-tasks"
              role="button"
              aria-expanded={sideNavCollapse.backoffice ? 'false' : 'true'}
              onClick={() => {
                setSideNavCollapse({ ...sideNavCollapse, backoffice: !sideNavCollapse.backoffice });
              }}
            >
              <div className="icon icon-sm shadow border-radius-md me-2 bg-white">
                <i className="fas fa-tasks"></i>
              </div>
              <span className="nav-link-text ms-1">タスク</span>
            </a>
            <div className={`collapse ${!sideNavCollapse.backoffice && 'show'}`} id="backoffice-tasks">
              <ul className="nav ms-4 ps-3">
                <li className="nav-item">
                  {(() => {
                    const path = `/w/${_w_!.identifier}/backoffice/quotation_tasks`;
                    const isActive =
                      location.pathname.startsWith(path) ||
                      (location.pathname.includes('/backoffice') && location.pathname.includes('/quotation_tasks'));
                    return (
                      <Link className={`nav-link ${isActive && 'active'}`} to={path} onClick={quotationTasksClick}>
                        <span className="sidenav-mini-icon">見</span>
                        <span className="sidenav-normal">見積申請</span>
                      </Link>
                    );
                  })()}
                </li>
                <li className="nav-item">
                  {(() => {
                    const path = `/w/${_w_!.identifier}/backoffice/sales_order_contract_tasks`;
                    const isActive =
                      location.pathname.startsWith(path) ||
                      (location.pathname.includes('/backoffice') &&
                        location.pathname.includes('/sales_order_contract_tasks'));
                    return (
                      <Link
                        className={`nav-link ${isActive && 'active'}`}
                        to={path}
                        onClick={salesOrderContractTasksClick}
                      >
                        <span className="sidenav-mini-icon">契</span>
                        <span className="sidenav-normal">契約申請（売上）</span>
                      </Link>
                    );
                  })()}
                </li>
                <li className="nav-item">
                  {(() => {
                    const path = `/w/${_w_!.identifier}/backoffice/invoice_tasks`;
                    const isActive =
                      location.pathname.startsWith(path) ||
                      (location.pathname.includes('/backoffice') && location.pathname.includes('/invoice_tasks'));
                    return (
                      <Link className={`nav-link ${isActive && 'active'}`} to={path} onClick={invoiceTasksClick}>
                        <span className="sidenav-mini-icon">請</span>
                        <span className="sidenav-normal">請求</span>
                      </Link>
                    );
                  })()}
                </li>
                <li className="nav-item">
                  {(() => {
                    const path = `/w/${_w_!.identifier}/backoffice/purchase_order_contract_tasks`;
                    const isActive =
                      location.pathname.includes('/backoffice') &&
                      location.pathname.includes('/purchase_order_contract_tasks');
                    return (
                      <Link
                        className={`nav-link ${isActive && 'active'}`}
                        to={path}
                        onClick={purchaseOrderContractTasksClick}
                      >
                        <span className="sidenav-mini-icon">契</span>
                        <span className="sidenav-normal">契約申請（費用）</span>
                      </Link>
                    );
                  })()}
                </li>
                <li className="nav-item">
                  {(() => {
                    const path = `/w/${_w_!.identifier}/backoffice/payment_tasks`;
                    const isActive =
                      location.pathname.includes('/backoffice') && location.pathname.includes('/payment_tasks');
                    return (
                      <Link className={`nav-link ${isActive && 'active'}`} to={path} onClick={paymentTasksClick}>
                        <span className="sidenav-mini-icon">支</span>
                        <span className="sidenav-normal">支払</span>
                      </Link>
                    );
                  })()}
                </li>
              </ul>
            </div>
          </li>
          {hasApproverRole && (
            <li className="nav-item">
              <Link
                className={`nav-link ${sideNavMenuSelectState.approval && 'active'}`}
                to={`/w/${_w_!.identifier}/approval`}
                onClick={approvalClick}
              >
                <div className="icon icon-sm shadow border-radius-md me-2 bg-white">
                  <i className="fas fa-stamp"></i>
                </div>
                <span className="nav-link-text ms-1">承認待ち</span>
              </Link>
            </li>
          )}
          {(hasAdminRole || hasFinancialOfficerRole) && (
            <li className="nav-item">
              <Link
                data-bs-toggle="collapse"
                to="#finance"
                className={`nav-link ${sideNavMenuSelectState.finance && 'active'}`}
                aria-controls="finance"
                role="button"
                aria-expanded={sideNavCollapse.finance ? 'false' : 'true'}
                onClick={() => {
                  setSideNavCollapse({ ...sideNavCollapse, finance: !sideNavCollapse.finance });
                }}
              >
                <div className="icon icon-sm shadow border-radius-md me-2 bg-white">
                  <i className="fas fa-cubes"></i>
                </div>
                <span className="nav-link-text ms-1">会計・経理</span>
              </Link>
              <div className={`collapse ${!sideNavCollapse.finance && 'show'}`} id="finance">
                <ul className="nav ms-4 ps-3">
                  <li className="nav-item">
                    {(() => {
                      const path = `/w/${_w_!.identifier}/finance/fixed_assets`;
                      const isActive = location.pathname.includes('/finance/fixed_assets');
                      return (
                        <Link className={`nav-link ${isActive && 'active'}`} to={path}>
                          <span className="sidenav-mini-icon">固</span>
                          <span className="sidenav-normal">固定資産管理</span>
                        </Link>
                      );
                    })()}
                  </li>
                  <li className="nav-item">
                    {(() => {
                      const path = `/w/${_w_!.identifier}/finance/finance_exports`;
                      const isActive = location.pathname.includes('/finance/finance_exports');
                      return (
                        <Link className={`nav-link ${isActive && 'active'}`} to={path}>
                          <span className="sidenav-mini-icon">デ</span>
                          <span className="sidenav-normal">データエクスポート</span>
                        </Link>
                      );
                    })()}
                  </li>
                </ul>
              </div>
            </li>
          )}
          <li className="nav-item">
            <hr className="horizontal dark" />
          </li>
          <li className="nav-item">
            <Link
              className={`nav-link ${sideNavMenuSelectState.setting && 'active'}`}
              to={`/w/${_w_!.identifier}/setting`}
            >
              <div className="icon icon-sm shadow border-radius-md me-2 bg-white">
                <i className="fas fa-cog"></i>
              </div>
              <span className="nav-link-text ms-1">スペース設定</span>
            </Link>
          </li>
        </ul>
      </div>
      <div className="sidenav-footer mx-3 mt-3 pt-3">
        <div className="card card-background shadow-none card-background-mask-secondary" id="sidenavCard">
          <div className="full-background" style={{ backgroundImage: `url(${helpImg})` }}></div>
          <div className="card-body text-start p-3 w-100">
            <div className="icon icon-sm bg-white shadow text-center border-radius-md d-flex align-items-center justify-content-center">
              <i className="fas fa-question-circle"></i>
            </div>
            <div className="docs-info mt-3">
              <h6 className="text-white up mb-0">困ったときは</h6>
              <a
                href="https://www.biz-node.app/rd/?wbfrd=faq"
                target="_blank"
                className="btn btn-white btn-sm w-100 mb-2"
                rel="noopener noreferrer"
              >
                FAQ
              </a>
              <a
                href="https://www.biz-node.app/rd/?wbfrd=manual"
                target="_blank"
                className="btn btn-white btn-sm w-100 mb-0"
                rel="noopener noreferrer"
              >
                マニュアル
              </a>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
};
